import { useCurrencyContext } from '@apeiron/currency'
import { CoreType, PlanetCardInfoType, PlanetLite } from '@apeiron/planet'
import {
  ApeironLinkButton,
  CARD_USD_OPTIONS,
  MayNull,
  SkeletonBox
} from '@apeiron/temp'
import { Box, styled } from '@mui/material'
import EthereumSVG from '@public/icons/icon-ethereum.svg'
import { CLASS_NAMES } from '@src/components/planet/share/planetCard/classes'
import PlanetCardImage from '@src/components/planet/share/planetCard/PlanetCardImage'
import PlanetCardStatus from '@src/components/planet/share/planetCard/PlanetCardStatus'
import { RelicsTokenList } from '@src/components/planet/share/RelicsTokenList'
import ApeironCardStatus from '@src/components/share/apeiron/ApeironCardStatus'
import PriceText from '@src/components/share/generic/PriceText'
import { GAAssetType } from '@src/constants/googleAnalytics'
import { usePlanetTypeContext } from '@src/contexts/planet/PlanetTypeContext'
import useTracking from '@src/hooks/common/analytics/useTracking'
import usePriceCalculation from '@src/hooks/common/tools/usePriceCalculation'
import { getRelicsTokens, isCore } from '@src/util/apeiron/planet'
import * as R from 'ramda'
import { FC, MouseEvent, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'

const Container = styled(SkeletonBox)`
  position: relative;
  aspect-ratio: 0.649;
  ${props => props.theme.breakpoints.up('res1024')} {
    width: 250px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    width: 156px;
  }
  &.Custom-Skeleton {
    aspect-ratio: 0.68;
    margin-bottom: 13px;
  }
`

const StyledButton = styled(ApeironLinkButton)`
  aspect-ratio: 0.649;
  position: relative;
  width: 95%;
`

const IdText = styled(Box)`
  color: #a5a7a6;

  ${props => props.theme.breakpoints.up('res1024')} {
    font-size: 12px;
    line-height: 14px;
    margin-bottom: 2px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    font-size: 10px;
    line-height: 10px;
  }
`

const NameText = styled(Box)`
  color: #ffffff;
  letter-spacing: 1%;
  margin-bottom: 2px;

  ${props => props.theme.breakpoints.up('res1024')} {
    font-size: 14px;
    line-height: 16px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    font-size: 12px;
    line-height: 12px;
  }
`

const InfoText = styled(Box)`
  color: #ffbf39;
  ${props => props.theme.breakpoints.up('res1024')} {
    font-size: 12px;
    line-height: 14px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    font-size: 10px;
    line-height: 10px;
  }
`

const CardImage = styled(PlanetCardImage)`
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`

const InfoHeader = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: column;
  position: absolute;
  ${props => props.theme.breakpoints.up('res1024')} {
    top: 2.5%;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    top: 2%;
  }
`

const InfoFooter = styled(ApeironCardStatus)`
  position: absolute;
  width: 100%;
  .Custom-Upper-Background {
    width: 97%;
    flex: 2;
  }
  .Custom-Lower-Background {
    width: 86%;
    flex: 1.3;
  }
  .Custom-Content {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
  }

  ${props => props.theme.breakpoints.up('res1024')} {
    height: 42px;
    bottom: 5px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    height: 28px;
    bottom: 3px;
  }
`

const EthPrice = styled(PriceText)`
  flex: 1.7;
  .Custom-Icon {
    ${props => props.theme.breakpoints.up('res1024')} {
      width: 18px;
      height: 18px;
    }
    ${props => props.theme.breakpoints.down('res1024')} {
      width: 15px;
      height: 15px;
    }
    margin-left: auto;
  }
  .Custom-Text {
    color: white;
    ${props => props.theme.breakpoints.up('res1024')} {
      font-size: 20px;
      font-weight: 400;
    }
    ${props => props.theme.breakpoints.down('res1024')} {
      font-size: 16px;
      font-weight: 400;
    }
  }
`

const FiatPrice = styled(PriceText)`
  flex: 1;
  padding-top: 4px;
  .Custom-Text {
    color: #999;
    ${props => props.theme.breakpoints.up('res1024')} {
      font-size: 14px;
      font-weight: 400;
    }
    ${props => props.theme.breakpoints.down('res1024')} {
      font-size: 12px;
      font-weight: 400;
    }
  }
`

const RelicsContainer = styled(Box)`
  position: absolute;
  bottom: 44px;
  left: 26px;
`

const PlanetInfoText: FC<PlanetInfoTextProps> = props => {
  const { infoType, planet } = props

  const { t } = useTranslation()

  switch (infoType) {
    case PlanetCardInfoType.Conjunction:
      return (
        <InfoText className='Custom-Info-Text'>
          {t('planet.card.breed_count', {
            breedCount: planet.breedCount
          })}
        </InfoText>
      )

    case PlanetCardInfoType.Age:
      return (
        <InfoText className='Custom-Info-Text'>
          {t('planet.card.age', {
            age: Math.round(planet.age)
          })}
        </InfoText>
      )

    case PlanetCardInfoType.Seed:
      return (
        <InfoText className='Custom-Info-Text'>
          {t('planet.card.cannot_trade')}
        </InfoText>
      )

    default:
      return <></>
  }
}

const PlanetCard: FC<Props> = props => {
  const {
    className,
    enableLink = true,
    infoType = PlanetCardInfoType.Conjunction,
    planet: rawPlanet,
    showInfo = true,
    showRelic = false,
    target = '_self',
    onClick,
    onPostProcess,
    onStatusClick,
    renderUrl
  } = props

  const { t } = useTranslation()

  const { gtmEmitViewItem } = useTracking()

  const { getApeironPrice } = useCurrencyContext()

  const planet = useMemo(() => {
    return !R.isNil(rawPlanet) && !R.isNil(onPostProcess)
      ? onPostProcess(rawPlanet)
      : rawPlanet
  }, [onPostProcess, rawPlanet])

  const { ref: inViewRef } = useInView({
    triggerOnce: true,
    onChange(inView) {
      if (inView) {
        // GA-qw3h75xu6e2YPSE
        gtmEmitViewItem({
          type: GAAssetType.Planet,
          item: planet
        })
      }
    }
  })

  const hasData = !R.isNil(planet)

  const { ethPrice, usdPrice } = usePriceCalculation(planet?.listingInfo)

  const formattedEthPrice = getApeironPrice(null, ethPrice)

  const formattedUsdPrice = getApeironPrice(null, usdPrice, CARD_USD_OPTIONS)

  const planetTypeContext = usePlanetTypeContext()

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (R.isNil(planet) || planet.disabled) {
        event?.preventDefault()

        return
      }

      if (onClick) {
        onClick(planet)
      }
    },
    [onClick, planet]
  )

  const planetTypeData = hasData
    ? planetTypeContext.getPlanetTypeData(planet.planetType)
    : undefined

  const buttonClassName = hasData
    ? [planet.disabled && CLASS_NAMES.disabled].filter(Boolean).join(' ')
    : ''

  const now = new Date()

  return (
    <Container className={className} loading={!hasData}>
      <Box ref={inViewRef} />
      {hasData ? (
        <>
          <StyledButton
            className={buttonClassName}
            enableLink={enableLink}
            target={target}
            url={renderUrl ? renderUrl(planet) : undefined}
            onClick={handleClick}
          >
            <CardImage
              coreType={planet.coreType ?? CoreType.None}
              disabled={planet.disabled || false}
              iconURL={planetTypeData?.planetTypeIcon}
              imageURL={planet.image ?? ''}
            />
            {showInfo && (
              <>
                <InfoHeader className='Custom-Info-Header'>
                  <IdText className='Custom-Id'>
                    {planet.seed
                      ? t('planet.card.seed')
                      : `# ${planet.planetID}`}
                  </IdText>
                  <NameText className='Custom-Name'>{planet.name}</NameText>
                  <PlanetInfoText infoType={infoType} planet={planet} />
                </InfoHeader>
                {planet.sale && (
                  <InfoFooter>
                    <EthPrice
                      icon={EthereumSVG}
                      displayPrice={formattedEthPrice}
                    />
                    <FiatPrice displayPrice={formattedUsdPrice} prefix='~$ ' />
                  </InfoFooter>
                )}
              </>
            )}
            {!isCore(planet) && showRelic && (
              <RelicsContainer>
                <RelicsTokenList
                  age={planet.age}
                  relicsTokens={getRelicsTokens(planet)}
                />
              </RelicsContainer>
            )}
          </StyledButton>
          <PlanetCardStatus
            planet={planet}
            now={now}
            arrow={!planet.seed}
            onClick={planet.seed ? undefined : onStatusClick}
          />
        </>
      ) : (
        <></>
      )}
    </Container>
  )
}

type PlanetInfoTextProps = {
  infoType: PlanetCardInfoType
  planet: PlanetLite
}

type Props = {
  className?: string
  enableLink?: boolean
  target?: '_self' | '_blank' | '_parent' | '_top'
  infoType?: PlanetCardInfoType
  planet: MayNull<PlanetLite>
  showInfo?: boolean
  showRelic?: boolean
  renderUrl?: (planet: PlanetLite) => string
  onClick?: (planet: PlanetLite) => void
  onPostProcess?: (planet: PlanetLite) => PlanetLite
  onStatusClick?: (planet: PlanetLite) => void
}

export default PlanetCard
